import React, { useState, useCallback, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Page, Pagination, Layout, Card, IndexTable, Text, Button, Box, Modal, Spinner, BlockStack, InlineStack, Tooltip, IndexFilters, useSetIndexFiltersMode } from '@shopify/polaris';
import { DeleteIcon, EmailIcon, PrintIcon, CalendarIcon } from '@shopify/polaris-icons';
import { useToast } from '#client/components/common/Toast/ToastContext';
import CustomBadge from '#client/components/common/CustomBadge/CustomBadge';
import { renderAndDownloadPDF } from '#client/pages/Voucher/voucherPDF';
import Breadcrumb from '#client/components/common/Breadcrumb/Breadcrumb';
import { PROGRAM_CONSTANTS } from '#client/pages/Programs/constants/programsConstants';
import DatePicker from '#client/pages/Programs/Program/components/ProgramInfo/DatePicker';
import { voucherService } from '#client/services/programs/programServices';
import { SessionContext } from '#client/contexts/SessionContext';
import { hasPermission } from '#client/utils/helpers';

const getStartOfDay = (date) => {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    return startOfDay;
};

const getMinimumExpirationDate = () => getStartOfDay(new Date());

const getMaximumExpirationDate = (program) => (program?.dateExpires ? getStartOfDay(new Date(program.dateExpires)) : null);

const getDefaultExpirationDate = (voucher, program) => {
    const today = getMinimumExpirationDate();
    const currentExpirationDate = getStartOfDay(new Date(voucher.dateExpires));
    const programExpirationDate = getMaximumExpirationDate(program);

    if (programExpirationDate && currentExpirationDate > programExpirationDate) {
        return programExpirationDate;
    }

    return currentExpirationDate >= today ? currentExpirationDate : today;
};

function VoucherList() {
    const { id: programId } = useParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isCustomer = pathname.startsWith('/customer');
    const { currentUser } = useContext(SessionContext);
    const canManageVouchers = isCustomer || hasPermission(currentUser, 'programEdit');
    const { addToast } = useToast();

    // data - using TanStack Query
    const { data, isLoading: loading } = useQuery({
        queryKey: ['vouchers', `program-${programId}`, isCustomer ? 'customer' : 'admin'],
        queryFn: () => voucherService.fetchVouchersWithProgram(programId, isCustomer),
        enabled: !!programId,
    });

    const vouchers = data?.vouchers || [];
    const program = data?.program || null;

    const [deactivateModal, setDeactivateModal] = useState({ open: false, voucherId: null });
    const [deactivateAllModal, setDeactivateAllModal] = useState({ open: false, voucherIds: [] });
    const [expirationModal, setExpirationModal] = useState({ open: false, voucher: null, expirationDate: null, error: '' });

    // pagination
    const [currentPage, setCurrentPage] = useState(1);

    // filters/search
    const [queryValue, setQueryValue] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [selected, setSelected] = useState(0);
    const { mode, setMode } = useSetIndexFiltersMode();

    // Mutations
    const deactivateMutation = useMutation({
        mutationFn: (voucherId) => voucherService.deactivateVoucher(voucherId, programId, isCustomer),
        onSuccess: () => {
            addToast({ type: 'success', message: 'Voucher deactivated successfully' });
            setDeactivateModal({ open: false, voucherId: null });
        },
        onError: () => {
            addToast({ type: 'error', message: 'Failed to deactivate voucher' });
            setDeactivateModal({ open: false, voucherId: null });
        },
    });

    const deactivateAllMutation = useMutation({
        mutationFn: (voucherIds) => voucherService.deactivateAllVouchers(voucherIds, programId, isCustomer),
        onSuccess: () => {
            addToast({ type: 'success', message: 'All vouchers deactivated successfully' });
            setDeactivateAllModal({ open: false, voucherIds: [] });
        },
        onError: () => {
            addToast({ type: 'error', message: 'Failed to deactivate all vouchers' });
            setDeactivateAllModal({ open: false, voucherIds: [] });
        },
    });

    const resendEmailMutation = useMutation({
        mutationFn: (voucherId) => voucherService.resendVoucherEmail(voucherId, isCustomer),
        onSuccess: () => {
            addToast({ type: 'success', message: 'Email resent successfully' });
        },
        onError: () => {
            addToast({ type: 'error', message: 'Failed to resend email' });
        },
    });

    const extendExpirationMutation = useMutation({
        mutationFn: ({ voucherId, expirationDate }) => voucherService.extendVoucherExpiration(voucherId, programId, expirationDate, isCustomer),
        onSuccess: () => {
            addToast({ type: 'success', message: 'Voucher expiration date updated' });
            setExpirationModal({ open: false, voucher: null, expirationDate: null, error: '' });
        },
        onError: (error) => {
            addToast({ type: 'error', message: error?.response?.data?.error || 'Failed to update voucher expiration date' });
        },
    });

    // applied filters
    const appliedFilters = statusFilter
        ? [
              {
                  key: 'status',
                  label: `Status: ${statusFilter}`,
                  onRemove: () => setStatusFilter(''),
              },
          ]
        : [];

    const handleQueryChange = useCallback((value) => setQueryValue(value), []);
    const handleQueryClear = useCallback(() => setQueryValue(''), []);
    const handleClearAll = useCallback(() => {
        setQueryValue('');
        setStatusFilter('');
    }, []);

    // actions
    const handleDeactivate = async () => {
        deactivateMutation.mutate(deactivateModal.voucherId);
    };
    const handleDeactivateAll = async () => {
        deactivateAllMutation.mutate(deactivateAllModal.voucherIds);
    };
    const handleOpenExpirationModal = (voucher) => {
        setExpirationModal({
            open: true,
            voucher,
            expirationDate: getDefaultExpirationDate(voucher, program),
            error: '',
        });
    };

    const handleCloseExpirationModal = () => {
        setExpirationModal({ open: false, voucher: null, expirationDate: null, error: '' });
    };

    const handleExtendExpiration = () => {
        const minimumExpirationDate = getMinimumExpirationDate();
        const maximumExpirationDate = getMaximumExpirationDate(program);
        const selectedExpirationDate = expirationModal.expirationDate ? getStartOfDay(expirationModal.expirationDate) : null;

        if (!selectedExpirationDate || selectedExpirationDate < minimumExpirationDate) {
            setExpirationModal((modal) => ({
                ...modal,
                error: `Choose a date on or after ${minimumExpirationDate.toLocaleDateString()}.`,
            }));
            return;
        }

        if (maximumExpirationDate && selectedExpirationDate > maximumExpirationDate) {
            setExpirationModal((modal) => ({
                ...modal,
                error: `Choose a date on or before ${maximumExpirationDate.toLocaleDateString()}.`,
            }));
            return;
        }

        extendExpirationMutation.mutate({
            voucherId: expirationModal.voucher.id,
            expirationDate: expirationModal.expirationDate.toISOString(),
        });
    };

    const downloadVoucher = async (voucher) => {
        try {
            const blob = await renderAndDownloadPDF([voucher], { returnBlob: true });
            if (!blob) throw new Error('No blob returned');
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (err) {
            console.error(err);
            addToast({ type: 'error', message: 'Failed to open voucher PDF. Downloading instead...' });
            renderAndDownloadPDF([voucher]);
        }
    };

    const resendVoucherEmail = async (voucherId) => {
        resendEmailMutation.mutate(voucherId);
    };

    if (loading) return <Spinner accessibilityLabel="Loading vouchers" size="large" />;

    // filtering logic
    const filteredVouchers = vouchers.filter((voucher) => {
        const matchesSearch =
            voucher.voucherCode?.toLowerCase().includes(queryValue.toLowerCase()) ||
            `${voucher.firstName ?? ''} ${voucher.lastName ?? ''}`.toLowerCase().includes(queryValue.toLowerCase()) ||
            voucher.distEmailAddress?.toLowerCase().includes(queryValue.toLowerCase());
        const matchesStatus = statusFilter ? voucher.status === statusFilter : true;
        return matchesSearch && matchesStatus;
    });

    // pagination calc
    const pageSize = 25;
    const totalPages = Math.ceil(filteredVouchers.length / pageSize);
    const paginatedVouchers = filteredVouchers.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + paginatedVouchers.length, filteredVouchers.length);

    const breadcrumbItems = [
        { label: 'Programs', url: isCustomer ? '/customer/programs' : '/program-management' },
        { label: program?.name || 'Program', url: isCustomer ? `/customer/programs/view/${programId}` : `/program-management/view/${programId}` },
        { label: 'Manage Vouchers' },
    ];
    const isPercentageSubsidy = program?.subsidyType === PROGRAM_CONSTANTS.SUBSIDY_TYPES.PERCENTAGE_SUBSIDY ? true : false;

    return (
        <>
            <Breadcrumb items={breadcrumbItems} />
            <Page fullWidth title="Voucher Management">
                <Layout>
                    {/* Program Header */}
                    <Layout.Section>
                        <Card>
                            <BlockStack align="space-between" blockalign="end">
                                <Box flex="1">
                                    <BlockStack gap="200">
                                        <Text variant="headingMd">Program Name: {program?.name ?? 'N/A'}</Text>
                                        <Text variant="headingSm" as="h3">
                                            Status: <CustomBadge status={program?.status?.charAt(0).toUpperCase() + program?.status?.slice(1).toLowerCase()} />
                                        </Text>
                                        <Text as="span">
                                            <Text as="span" variant="headingSm">
                                                Description:
                                            </Text>{' '}
                                            <Text as="span">{program?.programDescription ?? 'N/A'}</Text>
                                        </Text>
                                        <Text as="span">
                                            <Text as="span" variant="headingSm">
                                                Company:
                                            </Text>{' '}
                                            <Text as="span">
                                                {program?.company?.name ?? 'N/A'} | Account #: {program?.company?.netsuiteId ?? 'N/A'}
                                            </Text>
                                        </Text>
                                    </BlockStack>
                                </Box>
                                {canManageVouchers && program?.status?.toLowerCase() === 'active' && (
                                    <InlineStack align="end">
                                        <Button
                                            variant="primary"
                                            onClick={() => {
                                                navigate(isCustomer ? `/customer/voucher-distribution/${programId}` : `/voucher-distribution/${programId}`);
                                            }}
                                            disabled={program?.status?.toLowerCase() === 'archived' || program?.externallyManaged}
                                        >
                                            Create Voucher
                                        </Button>
                                    </InlineStack>
                                )}
                                {program?.externallyManaged && (
                                    <InlineStack align="end">
                                        <Text variant="bodyXs" tone="critical" as="p">
                                            Vouchers managed externally
                                        </Text>
                                    </InlineStack>
                                )}
                            </BlockStack>
                        </Card>
                    </Layout.Section>

                    {/* Filter Bar */}
                    <Layout.Section>
                        <Card>
                            <Box paddingBlock="400">
                                <Box paddingBlockEnd="400">
                                    <InlineStack align="space-between" blockAlign="center">
                                        <Text variant="headingLg" as="h2">
                                            Vouchers List
                                        </Text>
                                        {canManageVouchers && (
                                            <Tooltip content="Deactivate All Visible Vouchers">
                                                <Button
                                                    variant="primary"
                                                    icon={DeleteIcon}
                                                    onClick={() =>
                                                        setDeactivateAllModal({
                                                            open: true,
                                                            voucherIds: vouchers.filter((v) => v.status === 'active').map((v) => v.id),
                                                        })
                                                    }
                                                    disabled={vouchers.filter((v) => v.status === 'active').length === 0}
                                                >
                                                    Deactivate All
                                                </Button>
                                            </Tooltip>
                                        )}
                                    </InlineStack>
                                </Box>
                                <Card padding="0">
                                    <IndexFilters
                                        sortOptions={[]}
                                        sortSelected={[]}
                                        queryValue={queryValue}
                                        queryPlaceholder="Search vouchers"
                                        onQueryChange={handleQueryChange}
                                        onQueryClear={handleQueryClear}
                                        onSort={() => {}}
                                        cancelAction={{
                                            onAction: handleClearAll,
                                            disabled: false,
                                            loading: false,
                                        }}
                                        tabs={[]}
                                        selected={selected}
                                        onSelect={setSelected}
                                        filters={[
                                            {
                                                key: 'status',
                                                label: 'Status',
                                                filter: (
                                                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                                        <option value="">All</option>
                                                        <option value="active">Active</option>
                                                        <option value="inactive">Inactive</option>
                                                    </select>
                                                ),
                                            },
                                        ]}
                                        appliedFilters={appliedFilters}
                                        onClearAll={handleClearAll}
                                        mode={mode}
                                        setMode={setMode}
                                    />

                                    {/* Table */}
                                    <IndexTable
                                        resourceName={{ singular: 'voucher', plural: 'vouchers' }}
                                        itemCount={filteredVouchers.length}
                                        headings={[
                                            { title: 'Code' },
                                            { title: 'Name' },
                                            { title: 'Email' },
                                            { title: 'Status' },
                                            { title: 'Balance' },
                                            { title: 'Subsidy Amount' },
                                            { title: 'Expires' },
                                            { title: 'Created By' },
                                            { title: 'Actions' },
                                        ]}
                                        selectable={false}
                                    >
                                        {paginatedVouchers.map((voucher, index) => (
                                            <IndexTable.Row id={voucher.id} key={voucher.id} position={index}>
                                                <IndexTable.Cell>{voucher.voucherCode}</IndexTable.Cell>
                                                <IndexTable.Cell>{`${voucher.firstName ?? ''} ${voucher.lastName ?? ''}`}</IndexTable.Cell>
                                                <IndexTable.Cell>{voucher.distEmailAddress || 'N/A'}</IndexTable.Cell>
                                                <IndexTable.Cell>
                                                    <CustomBadge status={voucher.status?.charAt(0).toUpperCase() + voucher.status?.slice(1).toLowerCase()} />
                                                </IndexTable.Cell>
                                                <IndexTable.Cell>
                                                    {isPercentageSubsidy && 'N/A'}
                                                    {!isPercentageSubsidy && '$' + (voucher.valueRemaining?.toFixed(2) ?? '0.00')}
                                                </IndexTable.Cell>
                                                <IndexTable.Cell>
                                                    {isPercentageSubsidy && (voucher.valueInitial?.toFixed(2) ?? '0.00') + '%'}
                                                    {!isPercentageSubsidy && '$' + (voucher.valueInitial?.toFixed(2) ?? '0.00')}
                                                </IndexTable.Cell>
                                                <IndexTable.Cell>{new Date(voucher.dateExpires).toLocaleDateString()}</IndexTable.Cell>
                                                <IndexTable.Cell>{voucher.createdBy || '--'}</IndexTable.Cell>
                                                <IndexTable.Cell>
                                                    {canManageVouchers && voucher.status.toLowerCase() === 'active' && (
                                                        <>
                                                            <Tooltip content="Print">
                                                                <Button variant="tertiary" icon={PrintIcon} onClick={() => downloadVoucher(voucher)} />
                                                            </Tooltip>
                                                            <Tooltip content="Resend">
                                                                {voucher.distEmailAddress && <Button variant="tertiary" icon={EmailIcon} onClick={() => resendVoucherEmail(voucher.id)} />}
                                                            </Tooltip>
                                                            <Tooltip content="Deactivate">
                                                                <Button
                                                                    variant="tertiary"
                                                                    icon={DeleteIcon}
                                                                    destructive
                                                                    onClick={() =>
                                                                        setDeactivateModal({
                                                                            open: true,
                                                                            voucherId: voucher.id,
                                                                        })
                                                                    }
                                                                />
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                    {canManageVouchers && voucher.status.toLowerCase() === 'active' && program.status.toLowerCase() === 'active' && (
                                                        <>
                                                            <Tooltip content="Reset Expiration Date">
                                                                <Button variant="tertiary" icon={CalendarIcon} onClick={() => handleOpenExpirationModal(voucher)} />
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                    {canManageVouchers && voucher.status.toLowerCase() === 'inactive' && program.status.toLowerCase() === 'active' && (
                                                        <Tooltip content="Reset Expiration Date">
                                                            <Button variant="tertiary" icon={CalendarIcon} onClick={() => handleOpenExpirationModal(voucher)} />
                                                        </Tooltip>
                                                    )}
                                                </IndexTable.Cell>
                                            </IndexTable.Row>
                                        ))}
                                    </IndexTable>

                                    <Box padding="400">
                                        <Pagination
                                            hasPrevious={currentPage > 1}
                                            onPrevious={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                            hasNext={currentPage < totalPages}
                                            onNext={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                            label={`Showing ${startIndex + 1} - ${endIndex} of ${filteredVouchers.length}`}
                                        />
                                    </Box>
                                </Card>
                            </Box>
                        </Card>
                    </Layout.Section>
                </Layout>

                {/* Delete Modal */}
                <Modal
                    open={deactivateModal.open}
                    onClose={() => setDeactivateModal({ open: false, voucherId: null })}
                    title="Deactivate Voucher"
                    primaryAction={{
                        content: 'Deactivate',
                        onAction: handleDeactivate,
                        destructive: true,
                    }}
                    secondaryActions={[
                        {
                            content: 'Cancel',
                            onAction: () => setDeactivateModal({ open: false, voucherId: null }),
                        },
                    ]}
                >
                    <Modal.Section>
                        <Text>Are you sure you want to deactivate this voucher?</Text>
                    </Modal.Section>
                </Modal>

                {/* Extend Expiration Modal */}
                <Modal
                    open={expirationModal.open}
                    onClose={handleCloseExpirationModal}
                    title="Update Voucher Expiration"
                    primaryAction={{
                        content: 'Save',
                        onAction: handleExtendExpiration,
                        loading: extendExpirationMutation.isPending,
                        disabled: extendExpirationMutation.isPending,
                    }}
                    secondaryActions={[
                        {
                            content: 'Cancel',
                            onAction: handleCloseExpirationModal,
                        },
                    ]}
                >
                    <Modal.Section>
                        {expirationModal.voucher && expirationModal.expirationDate && (
                            <BlockStack gap="300">
                                <Text as="p">Choose a new expiration date for voucher {expirationModal.voucher.voucherCode}.</Text>
                                <Text as="p" tone="subdued">
                                    Current expiration: {new Date(expirationModal.voucher.dateExpires).toLocaleDateString()}
                                </Text>
                                {program?.dateExpires && (
                                    <Text as="p" tone="subdued">
                                        Program expiration: {new Date(program.dateExpires).toLocaleDateString()}
                                    </Text>
                                )}
                                <DatePicker
                                    label="New expiration date"
                                    value={expirationModal.expirationDate}
                                    onChange={(expirationDate) =>
                                        setExpirationModal((modal) => ({
                                            ...modal,
                                            expirationDate,
                                            error: '',
                                        }))
                                    }
                                    error={expirationModal.error}
                                    disableDatesBefore={getMinimumExpirationDate()}
                                    disableDatesAfter={getMaximumExpirationDate(program)}
                                />
                            </BlockStack>
                        )}
                    </Modal.Section>
                </Modal>

                {/* Deactivate All Modal */}
                <Modal
                    open={deactivateAllModal.open}
                    onClose={() => setDeactivateAllModal({ open: false, voucherIds: [] })}
                    title="Deactivate Vouchers"
                    primaryAction={{
                        content: 'Deactivate All',
                        onAction: handleDeactivateAll,
                        destructive: true,
                    }}
                    secondaryActions={[
                        {
                            content: 'Cancel',
                            onAction: () => setDeactivateAllModal({ open: false, voucherIds: [] }),
                        },
                    ]}
                >
                    <Modal.Section>
                        <Text>Are you sure you want to deactivate all visible vouchers?</Text>
                    </Modal.Section>
                </Modal>
            </Page>
        </>
    );
}

export default VoucherList;
